import React, { useEffect, useMemo, useState } from "react";
import OnboardingScreen, { OnboardingErrorText, OnboardingTitle } from "../OnboardingScreen";
import { OnboardingGlassButton, OnboardingNextButton } from "../OnboardingButtons";
import { StripeService } from "../../../services/stripeService";
import { COMPARE_SECTIONS, CompareValue, PAID_PLANS, PLAN_COPY } from "../planCatalog";

export type BillingPeriod = "free" | "month" | "quarter" | "year";

export interface PlanChoice {
  type: "free" | "plus" | "premium";
  name: "Free" | "Plus" | "Premium";
  billingPeriod: BillingPeriod;
  validatedCountry: string;
}

interface PlanStepProps {
  progress?: number | null;
  loading: boolean;
  error: string | null;
  onBack: () => void;
  onSelect: (plan: PlanChoice) => void;
}

type PaidPeriod = "month" | "year";
type PaidType = "plus" | "premium";

const formatUSD = (price: number) => `$${price.toFixed(2)}`;
const perMonth = (price: number, period: PaidPeriod) => (period === "month" ? price : price / 12);
const perWeek = (price: number, period: PaidPeriod) => (period === "month" ? price / 4.33 : price / 52);

/** iOS `SubscriptionTag`: Geist Medium 12, tracking 0.5, tinted capsule. */
const PlanTag: React.FC<{ label: string; tone: "brand" | "success" }> = ({ label, tone }) => (
  <span
    className={`inline-flex w-fit rounded-md px-2 py-0.5 font-geist text-[12px] font-medium tracking-[0.5px] ${
      tone === "brand" ? "bg-peak-brand-subtle text-peak-brand" : "bg-peak-success/[0.12] text-peak-success"
    }`}
  >
    {label}
  </span>
);

const BillingToggle: React.FC<{ period: PaidPeriod; onChange: (period: PaidPeriod) => void }> = ({ period, onChange }) => (
  <div className="flex gap-1 rounded-[10px] bg-peak-bg-secondary p-1" role="radiogroup" aria-label="Billing period">
    {([
      { value: "month" as const, label: "Monthly" },
      { value: "year" as const, label: "Yearly", tag: "SAVE 17%" },
    ]).map((option) => {
      const active = period === option.value;
      return (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={active}
          onClick={() => onChange(option.value)}
          className={`flex h-10 flex-1 items-center justify-center gap-2 rounded-lg font-funnel text-[14px] font-semibold transition-colors ${
            active ? "bg-peak-bg text-peak-text shadow-[0_1px_1px_rgba(0,0,0,0.06),0_1px_3px_rgba(0,0,0,0.1)]" : "text-peak-text-secondary"
          }`}
        >
          {option.label}
          {option.tag && <PlanTag label={option.tag} tone="success" />}
        </button>
      );
    })}
  </div>
);

const BreakdownPill: React.FC<{ amount: string; unit: string }> = ({ amount, unit }) => (
  <span className="rounded-md border border-peak-border bg-peak-bg-secondary px-2 py-1 font-geist text-[12px] text-peak-text-secondary">
    <strong className="font-semibold text-peak-text">{amount}</strong>/{unit}
  </span>
);

const PlanOption: React.FC<{
  name: string;
  blurb: string;
  price: number;
  period: PaidPeriod;
  popular: boolean;
  selected: boolean;
  onSelect: () => void;
}> = ({ name, blurb, price, period, popular, selected, onSelect }) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    aria-label={name}
    onClick={onSelect}
    className={`flex w-full items-start gap-3 rounded-xl p-4 text-left transition-colors ${
      selected ? "border-2 border-peak-brand bg-peak-brand-subtle" : "border border-peak-border-strong bg-peak-bg"
    }`}
  >
    <span
      className={`mt-0.5 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-2 ${
        selected ? "border-peak-brand" : "border-peak-border-strong"
      }`}
      aria-hidden="true"
    >
      {selected && <span className="h-3 w-3 rounded-full bg-peak-brand" />}
    </span>
    <span className="flex min-w-0 flex-1 flex-col gap-1">
      {popular && <PlanTag label="MOST POPULAR" tone="brand" />}
      <span className="font-funnel text-[16px] font-semibold text-peak-text">{name}</span>
      <span className="font-funnel text-[12px] leading-snug text-peak-text-secondary">{blurb}</span>
      <span className="mt-1.5 flex flex-wrap gap-1.5">
        {period === "year" && <BreakdownPill amount={formatUSD(perMonth(price, period))} unit="month" />}
        <BreakdownPill amount={formatUSD(perWeek(price, period))} unit="week" />
      </span>
    </span>
    <span className="flex shrink-0 flex-col items-end">
      <span className="font-funnel text-[20px] font-semibold text-peak-text">{formatUSD(price)}</span>
      <span className="font-funnel text-[12px] text-peak-text-secondary">{period === "year" ? "per year" : "per month"}</span>
    </span>
  </button>
);

const CompareCell: React.FC<{ value: CompareValue }> = ({ value }) => (
  <span className="flex h-11 w-[60px] shrink-0 items-center justify-center">
    {value === "included" ? (
      <svg viewBox="0 0 24 24" className="h-[15px] w-[15px] text-peak-brand" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-label="Included" role="img">
        <path d="M5 12.5l4.5 4.5L19 7" />
      </svg>
    ) : value === "excluded" ? (
      <span className="block h-0.5 w-3 rounded-sm bg-peak-text-tertiary/60" aria-label="Not included" role="img" />
    ) : (
      <span className="font-geist text-[16px] font-medium text-peak-text">{value.count}</span>
    )}
  </span>
);

/** iOS compare table: Free / Plus / Premium columns, Premium column tinted. */
const CompareTable: React.FC<{ plusMonthly: number; premiumMonthly: number }> = ({ plusMonthly, premiumMonthly }) => {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="relative" aria-label="Compare plans">
      <div className="pointer-events-none absolute inset-y-0 right-0 w-[60px] rounded-xl bg-peak-brand-subtle" aria-hidden="true" />
      <div className="relative">
        <div className="flex items-end border-b border-peak-border">
          <span className="flex-1 pb-3 font-funnel text-[12px] text-peak-text-tertiary">Per month</span>
          {[
            { name: "Free", price: "$0" },
            { name: "Plus", price: formatUSD(plusMonthly) },
            { name: "Premium", price: formatUSD(premiumMonthly) },
          ].map((head) => (
            <span key={head.name} className="flex w-[60px] shrink-0 flex-col items-center gap-0.5 pb-3 pt-4">
              <span className="font-funnel text-[14px] font-semibold text-peak-text">{head.name}</span>
              <span className="font-funnel text-[12px] text-peak-text-secondary">{head.price}</span>
            </span>
          ))}
        </div>
        {COMPARE_SECTIONS.map((section) => {
          const open = !collapsed.has(section.id);
          return (
            <div key={section.id}>
              <button
                type="button"
                onClick={() => toggle(section.id)}
                aria-expanded={open}
                className="flex w-full items-center gap-2 pb-1 pt-4 text-left"
              >
                <svg viewBox="0 0 24 24" className={`h-3 w-3 text-peak-text-secondary transition-transform ${open ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M6 9l6 6 6-6" />
                </svg>
                <span className="font-funnel text-[12px] font-medium tracking-[0.2px] text-peak-text-secondary">{section.title}</span>
              </button>
              {open &&
                section.features.map((feature) => (
                  <div key={feature.name} className="flex items-center border-b border-peak-border">
                    <span className="flex-1 py-3 pr-2 font-funnel text-[14px] text-peak-text">{feature.name}</span>
                    <CompareCell value={feature.free} />
                    <CompareCell value={feature.plus} />
                    <CompareCell value={feature.premium} />
                  </div>
                ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Plan step, styled after the iOS onboarding `SubscriptionView`: Monthly /
 * Yearly toggle, Plus and Premium radio rows, the feature comparison, and a
 * free fallback. Pricing is flat USD through the PEAK Stripe account.
 */
const PlanStep: React.FC<PlanStepProps> = ({ progress = null, loading, error, onBack, onSelect }) => {
  const [period, setPeriod] = useState<PaidPeriod>("month");
  const [chosen, setChosen] = useState<PaidType>("premium");
  const [showCompare, setShowCompare] = useState(false);
  const [validatedCountry, setValidatedCountry] = useState("United States");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ipCountry = await StripeService.detectCountryFromIp();
        const validated = await StripeService.validateCountryForPricing(ipCountry || undefined);
        if (!cancelled) setValidatedCountry(validated);
      } catch {
        /* fall back to US */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const prices = useMemo(
    () => ({
      plus: StripeService.getPeakDisplayPrice("plus", period),
      premium: StripeService.getPeakDisplayPrice("premium", period),
    }),
    [period],
  );
  const monthly = useMemo(
    () => ({
      plus: StripeService.getPeakDisplayPrice("plus", "month"),
      premium: StripeService.getPeakDisplayPrice("premium", "month"),
    }),
    [],
  );

  const selectedPlan = PAID_PLANS.find((plan) => plan.type === chosen) ?? PAID_PLANS[1];
  const cadence = period === "year" ? "yearly" : "monthly";

  const startTrial = () =>
    onSelect({ type: selectedPlan.type, name: selectedPlan.name, billingPeriod: period, validatedCountry });
  const chooseFree = () => onSelect({ type: "free", name: "Free", billingPeriod: "free", validatedCountry });

  return (
    <OnboardingScreen progress={progress} onBack={onBack} layout="scrollDesktop">
      <div className="flex flex-col gap-7">
        <OnboardingTitle title={PLAN_COPY.title} />

        <div className="flex flex-col gap-3">
          <BillingToggle period={period} onChange={setPeriod} />

          <div className="flex flex-col gap-3" role="radiogroup" aria-label="Plan">
            {PAID_PLANS.map((plan) => (
              <PlanOption
                key={plan.type}
                name={plan.name}
                blurb={plan.blurb}
                price={prices[plan.type]}
                period={period}
                popular={plan.type === "premium"}
                selected={chosen === plan.type}
                onSelect={() => setChosen(plan.type)}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={() => setShowCompare((open) => !open)}
            aria-expanded={showCompare}
            className="w-full py-2 text-center font-funnel text-[14px] font-semibold text-peak-brand"
          >
            {showCompare ? PLAN_COPY.hideCompare : PLAN_COPY.compare}
          </button>
          {showCompare && <CompareTable plusMonthly={monthly.plus} premiumMonthly={monthly.premium} />}

          <p className="text-center font-funnel text-[12px] text-peak-text-secondary">
            Billed {formatUSD(prices[chosen])} {cadence} after your free trial. Renews automatically unless cancelled.
            Manage or cancel any time in Settings › Subscription.
          </p>

          <OnboardingErrorText>{error}</OnboardingErrorText>

          <div className="flex flex-col gap-3 pt-2">
            <OnboardingNextButton
              label={loading ? PLAN_COPY.startingTrial : PLAN_COPY.startTrial}
              enabled={!loading}
              onClick={startTrial}
            />
            <OnboardingGlassButton label={PLAN_COPY.free} enabled={!loading} onClick={chooseFree} />
            <div className="flex justify-center gap-4 font-funnel text-[12px] font-medium text-peak-text-tertiary">
              <a href="/terms-of-service" target="_blank" rel="noreferrer">Terms</a>
              <a href="/privacy-policy" target="_blank" rel="noreferrer">Privacy</a>
            </div>
          </div>
        </div>
      </div>
    </OnboardingScreen>
  );
};

export default PlanStep;
